import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import { VerificationAgencyContactsService } from '../../../../Component/Api/VerificationAgency/VerificationAgencyContacts/VerificationAgencyContactsService';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import { MDButton } from '../../../../Component/Common/Components/MDButton';
import { ColumnSortOption } from '../../../../Component/Model/Dto/ColumnSortOption';
import { VerificationAgencyContacts } from '../../../../Component/Model/Dto/VerificationAgency/VerificationAgencyContacts';
import { EntriesPerPage, IBulkActions } from '../../../../Component/Model/Table/Table';
import { VerificationCompanyAtom } from '../../../../State/VerificationCompany.Atom';
import { VerificationAgencyContactTable } from '../ContactTable';
import { VAContactTableMapper } from '../ContactTableMapper';
import { ContactsModalExternalPage } from './ContactsModalExternalPage';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { ConfirmationDialog } from '../../../../Component/Common/Components/ConfirmationDialog';

interface IContacts {
    token: any;
    setHandleNextCount: (i: number) => void;
    handleNextCount: number;
}

export const ContactsExternalpage = (props: IContacts) => {
    const [company, setCompany] = useRecoilState(VerificationCompanyAtom);
    const [showContactModal, setShowContactModal] = React.useState<boolean>(false);
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [vaContactData, setVaContactData] = useState<VerificationAgencyContacts.ContactResult[]>([]);
    const [contactId, setContactId] = useState<number>();
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);

    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });

    const confirmDeleteMessage = "Are you sure you want to delete this contact?";

    const contactSearchRequest: VerificationAgencyContacts.VerificationAgencyContactTempAccessSearchRequest = {
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        searchValue: searchValue,
        vaClientId: company.vaClientId,
        token: props.token,
        columnSortOption: columnSortOptions
    }

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }
    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }
    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }
    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const fetchVaContacts = async () => {
        setIsLoading(true);
        try {
            const contactData = await VerificationAgencyContactsService.SearchExternalPage(contactSearchRequest);
            setVaContactData(contactData.data.results);
            setTotalItemsCount(contactData.data.totalItemsCount);
            setFilteredItemsCount(contactData.data.filteredItemsCount);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchVaContacts();
    }, [entriesPerPage, pageNumber, searchValue, columnSortOptions])

    const bulkActions: IBulkActions[] = [
        {
            label: 'Add Contact',
            action: () => setShowContactModal(true)
        }
    ]

    const showDeleteConfirmation = (contactId: number) => {
        setContactId(contactId);
        setShowDeleteConfirmationDialog(true);
    }

    const handleCloseConfirmation = () => {
        setShowDeleteConfirmationDialog(false);
        setContactId(undefined);
    }


    const deleteContact = async () => {
        if(contactId){
            try {
                await VerificationAgencyContactsService.DeleteExternalEntry(contactId, props.token);
                toast.success("Contact Deleted")
                fetchVaContacts();
            } catch (error) {
                console.log(error);
            } finally {
                handleCloseConfirmation();
            }
        }
    }

    const updateContact = (contactId: number) => {
        setContactId(contactId);
        setShowContactModal(true);
    }

    const closeContactModal = () => {
        setShowContactModal(false);
        setContactId(undefined);
        fetchVaContacts();
    }

    const ContactsTableMapper = VAContactTableMapper(vaContactData, showDeleteConfirmation, updateContact)
    return (
        <>
            <MDTypography color='#000000' variant='h5' fontWeight='bold' >
                Contacts
            </MDTypography>
            <MDTypography sx={{ fontSize: "15px", paddingBottom: "8px", paddingTop: "8px", color: "#727272" }}>
                Provide key personnel details for seamless communication between our agency and your organisation. Include names and contact information for those handling BEE compliance, engagement, and communication.
            </MDTypography>
            <VerificationAgencyContactTable
                bulkActions={bulkActions}
                entriesPerPage={entriesPerPage}
                entriesPerPageChanged={entriesPerPageChanged}
                filteredItemsCount={filteredItemsCount}
                isLoading={isLoading}
                pageNumberChanged={pageNumberChanged}
                searchValueChanged={searchValueChanged}
                results={ContactsTableMapper!}
                totalItemsCount={totalItemsCount}
                pageNumber={contactSearchRequest.pageNumber}
                columnSortingChanged={columnSortingChanged}
                isButtonOutlined = {true}
            />
            <MDBox mt={3} width='100%' display='flex' justifyContent='space-between'>
                <MDBox>
                    {<MDButton variant='outlined' color='secondary' onClick={() => { props.setHandleNextCount(props.handleNextCount - 1) }}>
                        Previous
                    </MDButton>}
                </MDBox>
                <MDBox>
                    <MDButton variant="gradient"
                        color="info" onClick={() => { props.setHandleNextCount(props.handleNextCount + 1) }}>
                        Next
                    </MDButton>
                </MDBox></MDBox>
            {showContactModal && <ContactsModalExternalPage showContactsModal={showContactModal} closeContactsModal={closeContactModal} contactId={contactId} token={props.token} />}

            {showDeleteConfirmationDialog && 
                <ConfirmationDialog
                    showDialog={showDeleteConfirmationDialog}
                    handleAccept={deleteContact}
                    handleCancel={handleCloseConfirmation}
                    heading={"Confirm Delete"}
                    message={confirmDeleteMessage}
                />
            }
        </>
    )
}

